import { CasesResult } from '@shared';
import { FilteredCase, SyncStatusEnum } from '@shared';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { Case, RegisteredCase, caseIdentifier, CasesType } from '@shared';
import { environment } from 'src/environments/environment';
import { SearchFilters } from 'src/app/shared/models/search-filters.model';

@Injectable({
  providedIn: 'root',
})
export class CasesService {
  private casesPath: string = `${environment.apiBasePath}${environment.casesPath}`;
  private specialChars = [
    ['+', '%2B'],
    [' ', '%20'],
  ];

  constructor(private http: HttpClient) { }

  public getCaseById(id: string): Observable<RegisteredCase> {
    return this.http.get<RegisteredCase>(`${this.casesPath}/${id}`);
  }

  public getCases({
    casesType = CasesType.ALLCASES,
    country,
    searchText,
    latestCaseId,
    recordsPerPage = 10,
    sortField = 'createdDate',
    sortDirection = 'desc',
    priority,
    returnTotalCases = true,
  }: SearchFilters): Observable<CasesResult<FilteredCase>> {
    let url = `${this.casesPath}/search/?caseType=${casesType}&pageLimit=${recordsPerPage}&sortField=${sortField}&sortDirection=${sortDirection}&returnTotalCases=${returnTotalCases}`;

    if (country) {
      url += `&countryName=${country}`;
    }
    if (latestCaseId) {
      url += `&latestCaseId=${latestCaseId}`;
    }
    if (priority) {
      url += `&priorityName=${priority}`;
    }
    if (searchText) {
      url += `&searchText=${this.replaceSpecialChars(searchText)}`;
    }

    return this.http.get<CasesResult<RegisteredCase>>(url).pipe(
      map((result) => {
        return {
          cases: this.transformCasesResult(result.cases),
          totalCases: result.totalCases ?? 0,
        };
      })
    );
  }

  public replaceSpecialChars(text: string) {
    this.specialChars.forEach((char) => {
      text = text.split(char[0]).join(char[1]);
    });
    return text;
  }

  private transformCasesResult(cases: RegisteredCase[]): FilteredCase[] {
    if (!Array.isArray(cases)) return [];
    return cases.map(
      (registeredCase: RegisteredCase) =>
        new FilteredCase(
          registeredCase.value.contactDetails.country,
          registeredCase.value.contactDetails.firstName,
          registeredCase.value.requestAssistance.priority,
          registeredCase.value.contactDetails.lastName,
          registeredCase.value.contactDetails.primaryPhone,
          registeredCase.value.contactDetails.accountNumber,
          registeredCase.id,

          registeredCase.createDate,


        )
    );
  }

  public createCase(model: Case): Observable<caseIdentifier> {
    return this.http.post<caseIdentifier>(`${this.casesPath}/`, model);
  }

  public updateCase(model: RegisteredCase, caseId: string): Observable<caseIdentifier> {
    return this.http.put<caseIdentifier>(`${this.casesPath}/${caseId}`, {
      lastUpdate: model.lastUpdate,
      value: model.value,
    });
  }

  public voidCase(caseId: string, reason: string): Observable<any> {
    return this.http.put(`${this.casesPath}/void/${caseId}`, {
      reason,
    });
  }

  public parseRegisteredCase(registeredCase: RegisteredCase): {} {
    const caseDetails = registeredCase.value.caseDetails;
    const contactDetails = registeredCase.value.contactDetails;
    const requestAssistance = registeredCase.value.requestAssistance;

    return {
      // case details
      inquirerCountry: caseDetails.countryId,
      businessUnit: caseDetails.businessUnit,
      caseCategory: caseDetails.caseCategory,
      caseSubCategory: caseDetails.caseSubCategory,
      inquirerRole: caseDetails.receivedFrom,
      subject: caseDetails.subject,
      description: caseDetails.description,

      // contact details
      contactType: contactDetails.contactType,
      firstName: contactDetails.firstName,
      lastName: contactDetails.lastName,
      accountNumber: contactDetails.accountNumber,
      accountName: contactDetails.accountName,
      country: contactDetails.country,
      street: contactDetails.street,
      city: contactDetails.city,
      state: contactDetails.stateProvince,
      postalCode: contactDetails.postalCode,
      primaryPhone: contactDetails.primaryPhone,
      phoneExt: contactDetails.phoneExt,
      isPrimary: contactDetails.primaryPhone.Number === contactDetails?.mobilePhone?.Number,
      mobilePhone: contactDetails.mobilePhone,
      email: contactDetails.email,
      fax: contactDetails.fax,
      contactLanguage: contactDetails.language,
      contactLanguageCode: contactDetails.languageCode,
      contactMethod: contactDetails.preferredChannel,
      openTime: contactDetails.openTime,
      closeTime: contactDetails.closeTime,

      // shipment Identifiers
      shipmentIdentifierValues: registeredCase.value.shippingTypeIdentifiers.map((x: any) => {
        return {
          label: x.identifier,
          apiName: x.identifier,
          value: x.value,
        };
      }),

      // request assistance
      contactRoleAssistance: requestAssistance.contactRole,
      priority: requestAssistance.priority,
      premierShipment: requestAssistance.premierShipment,
      //contactRequired: requestAssistance.contactRequired,
      contact: requestAssistance.contact,
      firstNameAssistance: requestAssistance.differentAddress.firstName,
      lastNameAssistance: requestAssistance.differentAddress.lastName,
      countryAssistance: requestAssistance.differentAddress.country,
      streetAssistance: requestAssistance.differentAddress.street,
      cityAssistance: requestAssistance.differentAddress.city,
      stateAssistance: requestAssistance.differentAddress.state,
      postalCodeAssistance: requestAssistance.differentAddress.postalCode,
      accountNumberAssistance: requestAssistance.differentAddress.accountNumber,
      primaryPhoneAssistance: requestAssistance.phoneNumber,
      phoneExtAssistance: requestAssistance.phoneExt,
      emailAssistance: requestAssistance.email,
      contactLanguageAssistance: requestAssistance.language,
      contactLanguageAssistanceCode: requestAssistance.languageCode,
      contactMethodAssistance: requestAssistance.preferred,
      caseInstructions: requestAssistance.assistance,
    };
  }

  public formatBody(model: any): Case {
    const formattedCase = {
      caseDetails: {
        countryId: model['inquirerCountry'],
        businessUnit: model['businessUnit'],
        caseCategory: model['caseCategory'],
        caseSubCategory: model['caseSubCategory'],
        receivedFrom: model['inquirerRole'],
        subject: model['subject'],
        description: model['description'],
      },
      contactDetails: {
        contactType: model['contactType'],
        firstName: model['firstName'],
        lastName: model['lastName'],
        accountNumber: model['accountNumber'],
        accountName: model['accountName'],
        country: model['country'],
        street: model['street'],
        city: model['city'],
        stateProvince: model['state'],
        postalCode: model['postalCode'],
        primaryPhone: model['primaryPhone'],
        phoneExt: model['phoneExt'],
        mobilePhone: model['isPrimary'] ? model['primaryPhone'] : model['mobilePhone'],
        email: model['email'],
        fax: model['fax'],
        language: model['contactLanguage'],
        languageCode: model['contactLanguageCode'],
        preferredChannel: model['contactMethod'],
        openTime: model['openTime'],
        closeTime: model['closeTime'],
      },
      shippingTypeIdentifiers: (model['shipmentIdentifierValues'] || []).map((x: any) => {
        return {
          identifier: x.apiName,
          value: x.value,
        };
      }),
      requestAssistance: {
        contactRole: model['contactRoleAssistance'],
        priority: model['priority'],
        premierShipment: model['premierShipment'],
        //contactRequired: model['contactRequired'],
        contact: model['contact'],
        differentAddress: {
          firstName: model['firstNameAssistance'],
          lastName: model['lastNameAssistance'],
          country: model['inquirerCountry'],
          street: model['streetAssistance'],
          city: model['cityAssistance'],
          state: model['stateAssistance'],
          postalCode: model['postalCodeAssistance'],
          accountNumber: model['accountNumberAssistance'],
        },
        phoneNumber: model['primaryPhoneAssistance'],
        phoneExt: model['phoneExtAssistance'],
        email: model['emailAssistance'],
        language: model['contactLanguageAssistance'],
        languageCode: model['contactLanguageAssistanceCode'],
        preferred: model['contactMethodAssistance'],
        assistance: model['caseInstructions'],
      },
    };

    if (model.sameAsCaller) {
      formattedCase.requestAssistance.differentAddress = {
        firstName: model['firstName'],
        lastName: model['lastName'],
        country: model['inquirerCountry'],
        street: model['street'],
        city: model['city'],
        state: model['state'],
        postalCode: model['postalCode'],
        accountNumber: model['accountNumber'],

      };
      formattedCase.requestAssistance.email = model['email'];
      formattedCase.requestAssistance.preferred = model['contactMethod'];
      formattedCase.requestAssistance.language = model['contactLanguage'];
      formattedCase.requestAssistance.priority = model['priority'];
    }

    return formattedCase;
  }
}
